import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Send, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * SupportWidget Component
 *
 * A floating support button that opens a modal for users to send support emails.
 * Available on all user pages via UserLayout.
 *
 * Features:
 * - Floating button fixed at bottom-right
 * - Modal with subject and message input fields
 * - Optional job selection dropdown (Apollo + Lead Finder jobs)
 * - Auto-includes user context (name, email, company, phone)
 * - Uses existing SMTP infrastructure
 */

// LocalStorage key for tracking first-time view
const SUPPORT_WIDGET_SEEN_KEY = 'supportWidgetSeen';

const SupportWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Check if user has seen the widget before
  useEffect(() => {
    const hasSeen = localStorage.getItem(SUPPORT_WIDGET_SEEN_KEY);
    if (!hasSeen) {
      // Show tooltip after a short delay for better UX
      const timer = setTimeout(() => {
        setShowTooltip(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Auto-hide tooltip after 8 seconds
  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
        localStorage.setItem(SUPPORT_WIDGET_SEEN_KEY, 'true');
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  // Dismiss tooltip when button is clicked
  const handleButtonClick = () => {
    if (showTooltip) {
      setShowTooltip(false);
      localStorage.setItem(SUPPORT_WIDGET_SEEN_KEY, 'true');
    }
    setIsOpen(true);
  };

  // Fetch user's jobs when modal opens
  useEffect(() => {
    if (isOpen && jobs.length === 0 && !loadingJobs) {
      fetchJobs();
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Delay reset to allow close animation
      const timer = setTimeout(() => {
        if (!success) {
          setSubject('');
          setMessage('');
          setSelectedJob(null);
          setError('');
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, success]);

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch both Apollo and Lead Finder jobs in parallel
      const [apolloRes, leadFinderRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/jobs/?limit=50`, { headers, timeout: 10000 }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/api/lead-finder/jobs?limit=50`, { headers, timeout: 10000 }).catch(() => ({ data: { jobs: [] } }))
      ]);

      const apolloJobs = (apolloRes.data || []).map(job => ({
        id: job.id,
        type: 'apollo',
        list_name: job.list_name || 'Untitled',
        status: job.status,
        created_at: job.created_at,
        label: `[Apollo] ${job.list_name || 'Untitled'} - ${job.status}`
      }));

      const leadFinderJobs = (leadFinderRes.data?.jobs || []).map(job => ({
        id: job.id,
        type: 'lead_finder',
        list_name: job.list_name || 'Untitled',
        status: job.status,
        created_at: job.created_at,
        label: `[Lead Finder] ${job.list_name || 'Untitled'} - ${job.status}`
      }));

      // Combine and sort by created_at (newest first)
      const allJobs = [...apolloJobs, ...leadFinderJobs].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setJobs(allJobs);
    } catch (err) {
      console.error('Failed to fetch jobs for support widget:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }

    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setSending(true);
    setError('');

    try {
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');

      const payload = {
        subject: subject.trim(),
        message: message.trim(),
        job_id: selectedJob?.id || null,
        job_type: selectedJob?.type || null
      };

      await axios.post(`${API_BASE_URL}/api/support/contact`, payload, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 15000
      });

      setSuccess(true);
      setSubject('');
      setMessage('');
      setSelectedJob(null);

      // Close modal after showing success
      setTimeout(() => {
        setSuccess(false);
        setIsOpen(false);
      }, 3000);

    } catch (err) {
      console.error('Failed to send support message:', err);
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatJobDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <>
      {/* Speech Bubble Tooltip - shows for first-time users */}
      {showTooltip && (
        <div
          onClick={handleButtonClick}
          style={{
            position: 'fixed',
            bottom: '90px',
            right: '24px',
            background: 'white',
            padding: '12px 16px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            zIndex: 1001,
            cursor: 'pointer',
            animation: 'tooltipFadeIn 0.3s ease-out',
            maxWidth: '200px'
          }}
        >
          <div style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--color-text-primary)',
            marginBottom: '4px'
          }}>
            Need help? 👋
          </div>
          <div style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)'
          }}>
            Click here to contact support
          </div>
          {/* Speech bubble pointer */}
          <div style={{
            position: 'absolute',
            bottom: '-8px',
            right: '32px',
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '8px solid white'
          }} />
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={handleButtonClick}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'var(--gradient-info)',
          border: 'none',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          transition: 'transform 0.2s, box-shadow 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
        }}
        title="Contact Support"
      >
        <MessageSquare size={24} color="white" />
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '20px'
          }}
        >
          {/* Modal Content */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '480px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'var(--gradient-info)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MessageSquare size={20} color="white" />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
                    Contact Support
                  </h2>
                  <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-muted)' }}>
                    We'll respond within 24 hours
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} color="#6b7280" />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {success ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '32px 20px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#dcfce7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <CheckCircle size={32} color="#16a34a" />
                  </div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
                    Message Sent!
                  </h4>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-muted)' }}>
                    We'll respond to <strong>{user?.email}</strong> within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Job Selection Dropdown */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--color-text-secondary)',
                      marginBottom: '6px'
                    }}>
                      Related Job <span style={{ color: 'var(--color-text-disabled)', fontWeight: '400' }}>(optional)</span>
                    </label>
                    <select
                      value={selectedJob ? `${selectedJob.type}:${selectedJob.id}` : ''}
                      onChange={(e) => {
                        if (!e.target.value) {
                          setSelectedJob(null);
                        } else {
                          const [type, id] = e.target.value.split(':');
                          const job = jobs.find(j => j.id === id && j.type === type);
                          setSelectedJob(job || null);
                        }
                      }}
                      disabled={loadingJobs}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        color: 'var(--color-text-secondary)',
                        cursor: loadingJobs ? 'wait' : 'pointer'
                      }}
                    >
                      <option value="">
                        {loadingJobs ? 'Loading jobs...' : '-- Select a job (optional) --'}
                      </option>
                      {jobs.map((job) => (
                        <option key={`${job.type}:${job.id}`} value={`${job.type}:${job.id}`}>
                          {job.type === 'lead_finder' ? '🔍' : '🚀'} {job.list_name} - {job.status.toUpperCase()} ({formatJobDate(job.created_at)})
                        </option>
                      ))}
                    </select>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--color-text-disabled)' }}>
                      Select a job if your query is about a specific extraction
                    </p>
                  </div>

                  {/* Subject Input */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--color-text-secondary)',
                      marginBottom: '6px'
                    }}>
                      Subject <span style={{ color: 'var(--color-danger)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="e.g., Issue with my recent extraction"
                      maxLength={200}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: '6px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  {/* Message Textarea */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--color-text-secondary)',
                      marginBottom: '6px'
                    }}>
                      Message <span style={{ color: 'var(--color-danger)' }}>*</span>
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Describe your issue or question in detail..."
                      rows={4}
                      maxLength={5000}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: '6px',
                        resize: 'vertical',
                        minHeight: '100px',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit'
                      }}
                    />
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--color-text-disabled)', textAlign: 'right' }}>
                      {message.length}/5000
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      background: 'var(--color-danger-bg)',
                      border: '1px solid var(--color-danger-border)',
                      borderRadius: '6px',
                      marginBottom: '16px'
                    }}>
                      <AlertCircle size={16} color="#dc2626" />
                      <span style={{ fontSize: '13px', color: 'var(--color-danger-fg)' }}>{error}</span>
                    </div>
                  )}

                  {/* Info Note */}
                  <div style={{
                    padding: '12px',
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '6px',
                    marginBottom: '16px'
                  }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#0369a1' }}>
                      ℹ️ Your account details will be included automatically for faster assistance.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={sending || !subject.trim() || !message.trim()}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: 'white',
                      background: sending || !subject.trim() || !message.trim()
                        ? 'var(--color-text-disabled)'
                        : 'var(--gradient-info)',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: sending || !subject.trim() || !message.trim() ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {sending ? (
                      <>
                        <span style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid white',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite'
                        }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default SupportWidget;
